import React, { useRef, useState } from "react";
import DocumentImageValidator from "./documentImageValidator";

// *** UMBRALES MÁS ESTRICTOS ***
const DEFAULTS = {
  minBlurScore: 950, // ↑ exige más nitidez
  maxClippedHiPct: 2, // ↓ menos píxeles quemados
  maxClippedLoPct: 6, // ↓ menos píxeles negros
  minMean: 40, // ↑ imagen no tan oscura
  maxMean: 220, // ↓ imagen no tan brillante
  maxBorderPct: 5, // ↓ menos contenido tocando borde
  tolerancePct: 0.1, // banda de tolerancia (10%) para mensajes "casi"
};

// Utilidad: crea un Error con metadata
function makeError(message, code, details) {
  const err = new Error(message);
  err.name = "DocValidationError";
  err.code = code;
  err.details = details; // { metrics, reasons, warnings }
  return err;
}

function decide(result, limits) {
  const L = { ...DEFAULTS, ...(limits || {}) };
  const { blurScore, exposure = {}, edgesTouchingBorder } = result || {};

  const reasonsHard = [];
  const reasonsSoft = [];
  const advice = [];

  const within = (value, limit, cmp, tolPct = L.tolerancePct) => {
    const tol = Math.abs(limit) * tolPct;
    if (cmp === "min") return value >= limit - tol && value < limit;
    if (cmp === "max") return value <= limit + tol && value > limit;
    return false;
  };

  // ----- NITIDEZ -----
  if (Number.isFinite(blurScore)) {
    if (blurScore < L.minBlurScore) {
      const msgUser = "La imagen está borrosa.";
      const msgSoft = "La imagen está ligeramente borrosa.";
      if (within(blurScore, L.minBlurScore, "min")) {
        reasonsSoft.push(msgSoft);
      } else {
        reasonsHard.push(msgUser);
      }
      advice.push(
        "Acerca la cámara, mantén firme el teléfono y enfoca el texto."
      );
    }
  } else {
    reasonsHard.push("No pudimos evaluar la nitidez de la imagen.");
  }

  // ----- EXPOSICIÓN ALTA -----
  const hiPct = exposure.clippedHighlights ?? NaN;
  if (
    (exposure.mean ?? Infinity) > L.maxMean ||
    (hiPct ?? 0) > L.maxClippedHiPct
  ) {
    const msgUser = "La imagen está demasiado brillante.";
    const msgSoft = "La imagen está un poco brillante.";
    if (
      within(exposure.mean ?? 0, L.maxMean, "max") ||
      within(hiPct ?? 0, L.maxClippedHiPct, "max")
    ) {
      reasonsSoft.push(msgSoft);
    } else {
      reasonsHard.push(msgUser);
    }
    advice.push(
      "Evita el flash y los reflejos; prueba con luz natural o inclina el teléfono."
    );
  }

  // ----- EXPOSICIÓN BAJA -----
  const loPct = exposure.clippedShadows ?? NaN;
  if (
    (exposure.mean ?? -Infinity) < L.minMean ||
    (loPct ?? 0) > L.maxClippedLoPct
  ) {
    const msgUser = "La imagen está demasiado oscura.";
    const msgSoft = "La imagen está un poco oscura.";
    if (
      within(exposure.mean ?? 0, L.minMean, "min") ||
      within(loPct ?? 0, L.maxClippedLoPct, "max")
    ) {
      reasonsSoft.push(msgSoft);
    } else {
      reasonsHard.push(msgUser);
    }
    advice.push("Usa mejor iluminación y evita el contraluz.");
  }

  // ----- BORDES / RECORTE -----
  if (
    Number.isFinite(edgesTouchingBorder) &&
    edgesTouchingBorder > L.maxBorderPct
  ) {
    const msgUser = "El documento está recortado o sin márgenes.";
    const msgSoft = "El documento está muy pegado a los bordes.";
    if (within(edgesTouchingBorder, L.maxBorderPct, "max")) {
      reasonsSoft.push(msgSoft);
    } else {
      reasonsHard.push(msgUser);
    }
    advice.push("Deja márgenes visibles alrededor del documento.");
  }

  // ----- POLÍTICA (¡AQUÍ DEFINIMOS ok!) -----
  const ok = reasonsHard.length === 0;

  if (!ok) {
    const lines = [
      "Tu imagen fue rechazada por:",
      ...reasonsHard.map((r) => `• ${r}`),
    ];
    if (reasonsSoft.length) {
      lines.push("", "Observaciones:", ...reasonsSoft.map((r) => `• ${r}`));
    }
    if (advice.length) {
      lines.push(
        "",
        "Sugerencias:",
        ...[...new Set(advice)].map((a) => `• ${a}`)
      );
    }
    const message = lines.join("\n");

    const err = new Error(message);
    err.name = "DocValidationError";
    err.code = "DOC_VALIDATION_FAIL";
    err.details = {
      metrics: {
        blurScore: Math.round(blurScore || 0),
        mean: Math.round(exposure.mean || 0),
        clippedHighlights: Number.isFinite(hiPct)
          ? Number(hiPct.toFixed(1))
          : null,
        clippedShadows: Number.isFinite(loPct)
          ? Number(loPct.toFixed(1))
          : null,
        edgesTouchingBorder: Number.isFinite(edgesTouchingBorder)
          ? Number(edgesTouchingBorder.toFixed(1))
          : null,
      },
      reasons: [...reasonsHard],
      warnings: [...reasonsSoft],
      advice: [...new Set(advice)],
      thresholds: L,
    };
    return { ok: false, error: err };
  }

  // Caso de éxito: devuelve ok y warnings (si quieres mostrarlos como toast)
  return { ok: true, warnings: reasonsSoft };
}

const DocumentQualityGate = ({
  onPass,
  onFail,
  children,
  docOptions,
  accept = ".jpg,.jpeg,.png,.pdf",
  pdfPolicy = "allow", // "allow" | "reject"
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [limits, setLimits] = useState(null);

  const openPicker = () => inputRef.current?.click();

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type === "application/pdf") {
      if (pdfPolicy === "reject") {
        onFail?.(
          makeError(
            "El PDF no puede validarse como imagen. Sube JPG/PNG.",
            "PDF_REJECTED",
            {}
          )
        );
      } else {
        onPass?.(file); // pasa directo (sin validar como imagen)
      }
      e.target.value = "";
      return;
    }

    if (!file.type.startsWith("image/")) {
      onFail?.(
        makeError(
          "Formato no soportado. Usa JPG/PNG o PDF.",
          "FORMAT_UNSUPPORTED",
          {}
        )
      );
      e.target.value = "";
      return;
    }

    setBusy(true);
    setLimits(docOptions || null);
    setPendingFile(file);
  };

  const handleValidation = (result) => {
    try {
      const decision = decide(result, limits);
      if (!decision.ok) {
        onFail?.(decision.error);
      } else {
        // (Opcional) mostrar warnings no bloqueantes:
        // if (decision.warnings?.length) console.warn("Advertencias:", decision.warnings, decision.details);
        onPass?.(pendingFile);
      }
    } finally {
      setBusy(false);
      setPendingFile(null);
      setLimits(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={handleChange}
      />
      {children?.({ openPicker, busy })}
      {pendingFile && (
        <DocumentImageValidator
          file={pendingFile}
          onResult={handleValidation}
        />
      )}
    </>
  );
};

export default DocumentQualityGate;
